import { Box, Grid } from '@mui/material';
import {
  RevenueMetrics,
  MRRBreakdown,
  RevenueTrendDataPoint,
  RevenueByTierDataPoint,
  ChurnAnalysis,
} from '../../../../types/admin';
import RefreshButton from '../../system/shared/RefreshButton';
import AdminMetricCard from '../../shared/AdminMetricCard';
import MRRBreakdownChart from './MRRBreakdownChart';
import RevenueTrendChart from './RevenueTrendChart';
import RevenueByTierChart from './RevenueByTierChart';
import ChurnAnalysisTable from './ChurnAnalysisTable';
import { formatCurrency } from '../../../../utils/formatters';

interface RevenueAnalyticsTabProps {
  revenueMetrics: RevenueMetrics;
  mrrBreakdown: MRRBreakdown;
  revenueTrend: RevenueTrendDataPoint[];
  revenueByTier: RevenueByTierDataPoint[];
  churnAnalysis: ChurnAnalysis[];
  loading?: boolean;
  lastUpdated: string;
  onRefresh: () => void;
}

export default function RevenueAnalyticsTab({
  revenueMetrics,
  mrrBreakdown,
  revenueTrend,
  revenueByTier,
  churnAnalysis,
  loading,
  lastUpdated,
  onRefresh,
}: RevenueAnalyticsTabProps) {
  return (
    <Box>
      {/* Header with Refresh Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
        <RefreshButton onRefresh={onRefresh} lastUpdated={lastUpdated} loading={loading} />
      </Box>

      {/* Revenue Metrics */}
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={2.4}>
          <AdminMetricCard
            title="MRR"
            value={formatCurrency(revenueMetrics.mrr)}
            trend={{
              value: Math.abs(revenueMetrics.mrr_change),
              label: `${revenueMetrics.mrr_change > 0 ? '+' : ''}${revenueMetrics.mrr_change}%`,
              direction: revenueMetrics.mrr_change >= 0 ? 'up' : 'down',
            }}
            subtitle="Monthly Recurring Revenue"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <AdminMetricCard
            title="ARR"
            value={formatCurrency(revenueMetrics.arr)}
            trend={{
              value: Math.abs(revenueMetrics.arr_change),
              label: `${revenueMetrics.arr_change > 0 ? '+' : ''}${revenueMetrics.arr_change}%`,
              direction: revenueMetrics.arr_change >= 0 ? 'up' : 'down',
            }}
            subtitle="Annual Recurring Revenue"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <AdminMetricCard
            title="ARPU"
            value={formatCurrency(revenueMetrics.arpu)}
            trend={{
              value: Math.abs(revenueMetrics.arpu_change),
              label: `${revenueMetrics.arpu_change > 0 ? '+' : ''}${revenueMetrics.arpu_change}%`,
              direction: revenueMetrics.arpu_change >= 0 ? 'up' : 'down',
            }}
            subtitle="Avg Revenue Per User"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <AdminMetricCard
            title="LTV"
            value={formatCurrency(revenueMetrics.ltv)}
            trend={{
              value: Math.abs(revenueMetrics.ltv_change),
              label: `${revenueMetrics.ltv_change > 0 ? '+' : ''}${revenueMetrics.ltv_change}%`,
              direction: revenueMetrics.ltv_change >= 0 ? 'up' : 'down',
            }}
            subtitle="Customer Lifetime Value"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <AdminMetricCard
            title="NRR"
            value={`${revenueMetrics.nrr}%`}
            trend={{
              value: Math.abs(revenueMetrics.nrr_change),
              label: `${revenueMetrics.nrr_change > 0 ? '+' : ''}${revenueMetrics.nrr_change}%`,
              direction: revenueMetrics.nrr_change >= 0 ? 'up' : 'down',
            }}
            subtitle="Net Revenue Retention"
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* MRR Breakdown */}
      <Box sx={{ mt: 4 }}>
        <MRRBreakdownChart data={mrrBreakdown} loading={loading} />
      </Box>

      {/* Charts */}
      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12} lg={6}>
          <RevenueTrendChart data={revenueTrend} loading={loading} />
        </Grid>
        <Grid item xs={12} lg={6}>
          <RevenueByTierChart data={revenueByTier} loading={loading} />
        </Grid>
      </Grid>

      {/* Churn Analysis */}
      <Box sx={{ mt: 4 }}>
        <ChurnAnalysisTable data={churnAnalysis} loading={loading} />
      </Box>
    </Box>
  );
}
